// Incident Management Types

export type InjurySeverity = 
  | "no_injury" 
  | "first_aid" 
  | "medical_treatment" 
  | "lost_time" 
  | "fatality";

export type AreaSafetyStatus = 
  | "secured" 
  | "temporary_control" 
  | "still_unsafe";

export type IncidentStatus = 
  | "reported" 
  | "investigating" 
  | "actions_pending" 
  | "verification" 
  | "closed";

export type ControlType = 
  | "engineering" 
  | "administrative" 
  | "supervision" 
  | "behavioral";

export type ActionStatus = 
  | "pending" 
  | "in_progress" 
  | "completed" 
  | "verified";

export type FailedControl = 
  | "engineering"
  | "physical_guard"
  | "procedure"
  | "supervision"
  | "planning"
  | "fatigue_management"
  | "communication"
  | "training"
  | "permit";

export type ContributingCondition = 
  | "time_pressure"
  | "work_overload"
  | "environmental"
  | "equipment_unavailable"
  | "poor_layout"
  | "staffing_shortage"
  | "conflicting_targets";

export type TimelineEventType = 
  | "task_start"
  | "deviation"
  | "incident_moment"
  | "emergency_response"
  | "stabilization"
  | "sorin_signal"
  | "investigation"
  | "action_taken";

export interface Issue {
  id: string;
  company_id: string;
  site_id?: string;
  location: string;
  hazard_category?: string;
  status: "exposure" | "near_miss" | "harm_event" | "closed";
  first_detected_at: string;
  harm_occurred_at?: string;
  linked_cluster_id?: string;
  created_at: string;
  updated_at: string;
}

// AI Extracted Context from incident description
export interface AIExtractedPerson {
  name: string;
  worker_id: string | null;
  role_in_incident: "injured" | "witness" | "involved" | "reporter";
  matched_confidence: number;
}

export interface AIExtractedSignal {
  type: "hazard_report" | "worker_diagnostic" | "cluster" | "inspection_finding";
  id: string;
  title: string;
  relevance: string;
  risk_score?: number;
}

export interface AIExtractedContext {
  people_involved: AIExtractedPerson[];
  mechanism_of_injury: string | null;
  hazard_category: string | null;
  body_part: string | null;
  work_activity: string | null;
  location: string | null;
  inferred_severity: InjurySeverity | null;
  linked_signals: AIExtractedSignal[];
  generated_at?: string;
}

export interface WorkplaceIncident {
  id: string;
  issue_id?: string;
  company_id: string;
  site_id?: string;
  primary_report_id?: string;
  
  // Event details
  incident_datetime: string;
  location: string;
  shift?: string;
  description?: string;
  
  // Injury details
  injury_severity: InjurySeverity;
  body_part?: string;
  mechanism_of_injury?: string;
  immediate_treatment?: string;
  lost_time_days?: number;
  
  // Regulatory
  lost_time_indicator: boolean;
  is_wajib_lapor: boolean;
  regulator_notified_at?: string;
  
  // Area safety
  area_safety_status: AreaSafetyStatus;
  
  // People
  reported_by?: string;
  people_involved?: string[];
  
  // Media
  photo_urls?: string[];
  voice_transcript?: string;
  
  // Status
  status: IncidentStatus;
  
  // SORIN insights
  related_hazard_count: number;
  prior_cluster_risk?: number;
  prior_friction_score?: number;
  was_predictable: boolean;
  
  // AI Extracted Context
  ai_extracted_context?: AIExtractedContext;
  
  created_at: string;
  updated_at: string;
  
  // Joined data
  reporter?: {
    id: string;
    display_name: string;
  };
  site?: {
    id: string;
    location_name: string;
  };
}

export interface IncidentInvestigation {
  id: string;
  incident_id: string;
  failed_controls: FailedControl[];
  contributing_conditions: ContributingCondition[];
  learning_statement: string;
  learning_statement_ai_rewritten?: string;
  investigated_by?: string;
  investigated_at: string;
  created_at: string;
  
  // Joined data
  investigator?: {
    id: string;
    display_name: string;
  };
}

export interface IncidentAction {
  id: string;
  incident_id: string;
  control_type: ControlType;
  action_description: string;
  owner_id?: string;
  due_date?: string;
  verification_method?: string;
  linked_issue_id?: string;
  linked_cluster_id?: string;
  linked_supervisor_task_id?: string;
  evidence_photo_url?: string;
  status: ActionStatus;
  completed_at?: string;
  verified_at?: string;
  verified_by?: string;
  created_at: string;
  updated_at: string;
  
  // Joined data
  owner?: {
    id: string;
    display_name: string;
  };
}

export interface IncidentTimelineEvent {
  id: string;
  incident_id: string;
  event_type: TimelineEventType;
  event_datetime: string;
  description: string;
  source?: string;
  created_by?: string;
  created_at: string;
}

// Form data for reporting incidents
export interface IncidentReportData {
  description: string;
  injury_severity: InjurySeverity;
  body_part?: string;
  mechanism_of_injury?: string;
  immediate_treatment?: string;
  location: string;
  incident_datetime: string;
  shift?: string;
  area_safety_status: AreaSafetyStatus;
  photo_urls?: string[];
  voice_transcript?: string;
  people_involved?: string[];
  hazard_category?: string;
}

// Labels for UI display
export const INJURY_SEVERITY_LABELS: Record<InjurySeverity, { label: string; labelId: string; icon: string; color: string }> = {
  no_injury: { label: "No Injury (Near Miss)", labelId: "Tidak Ada Cedera", icon: "✓", color: "text-green-600" },
  first_aid: { label: "First Aid Only", labelId: "P3K Saja", icon: "🩹", color: "text-yellow-600" },
  medical_treatment: { label: "Medical Treatment", labelId: "Perlu Perawatan Medis", icon: "🏥", color: "text-orange-600" },
  lost_time: { label: "Lost Time Injury", labelId: "Tidak Bisa Bekerja", icon: "🚑", color: "text-red-600" },
  fatality: { label: "Fatality", labelId: "Meninggal Dunia", icon: "⚫", color: "text-gray-900" },
};

export const AREA_SAFETY_LABELS: Record<AreaSafetyStatus, { label: string; labelId: string; icon: string; color: string }> = {
  secured: { label: "Area Secured", labelId: "Area Sudah Aman", icon: "✅", color: "text-green-600" },
  temporary_control: { label: "Temporary Control", labelId: "Ada Pengendalian Sementara", icon: "⚠️", color: "text-yellow-600" },
  still_unsafe: { label: "Still Unsafe", labelId: "Masih Berbahaya", icon: "🚨", color: "text-red-600" },
};

export const FAILED_CONTROL_LABELS: Record<FailedControl, string> = {
  engineering: "Rekayasa Teknik",
  physical_guard: "Pelindung Fisik",
  procedure: "Prosedur/SOP",
  supervision: "Pengawasan",
  planning: "Perencanaan Kerja",
  fatigue_management: "Manajemen Kelelahan",
  communication: "Komunikasi",
  training: "Pelatihan",
  permit: "Izin Kerja",
};

export const CONTRIBUTING_CONDITION_LABELS: Record<ContributingCondition, string> = {
  time_pressure: "Tekanan Waktu",
  work_overload: "Beban Kerja Berlebih",
  environmental: "Kondisi Lingkungan",
  equipment_unavailable: "Peralatan Tidak Tersedia",
  poor_layout: "Tata Letak Buruk",
  staffing_shortage: "Kekurangan Tenaga Kerja",
  conflicting_targets: "Target yang Bertentangan",
};

export const CONTROL_TYPE_LABELS: Record<ControlType, { label: string; color: string }> = {
  engineering: { label: "Rekayasa", color: "bg-blue-100 text-blue-800" },
  administrative: { label: "Administratif", color: "bg-purple-100 text-purple-800" },
  supervision: { label: "Pengawasan", color: "bg-orange-100 text-orange-800" },
  behavioral: { label: "Perilaku", color: "bg-green-100 text-green-800" },
};
